import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { userApi } from '../services/userApi'
import type { LoginCredentials, RegisterData, AuthResponse, User, OAuthProvider, SetCredentialsData, LinkOAuthData } from '../types'
export const useLogin = () => {
  const queryClient = useQueryClient()

  return useMutation<AuthResponse, Error, LoginCredentials>({
    mutationFn: userApi.login,
    onSuccess: (data, variables) => {
      // Clear all React Query caches before setting new user data
      queryClient.clear()

      // Save token to localStorage
      localStorage.setItem('authToken', data.access_token)
      localStorage.setItem('loginTime', new Date().toISOString())

      // Handle remember me
      if (variables.remember_me) {
        localStorage.setItem('rememberMe', 'true')
      } else {
        localStorage.removeItem('rememberMe')
      }

      // Set user data to query cache to avoid additional API call
      if (data.user) {
        queryClient.setQueryData(['profile'], data.user)
      }
    },
  })
}

export const useRegister = () => {
  return useMutation<User, Error, RegisterData>({
    mutationFn: userApi.register,
  })
}

export const useOAuthLogin = () => {
  return useMutation<{ authorization_url: string; state: string; provider: string }, Error, string>({
    mutationFn: (provider: string) => userApi.getOAuthUrl(provider),
    onSuccess: (data) => {
      console.log('🔍 [OAuth-Frontend] 收到后端响应:', data)
      console.log('🔍 [OAuth-Frontend] provider字段:', data.provider)
      console.log('🔍 [OAuth-Frontend] state字段:', data.state)
      console.log('🔍 [OAuth-Frontend] authorization_url字段:', data.authorization_url)

      // Validate provider field
      if (!data.provider) {
        console.error('❌ [OAuth-Frontend] provider字段为空!')
        throw new Error('无法确定OAuth提供商')
      }

      // Save state for callback verification
      sessionStorage.setItem('oauth_state', data.state)
      console.log('✅ [OAuth-Frontend] 已保存state到sessionStorage')

      // Redirect to OAuth provider
      console.log('🚀 [OAuth-Frontend] 准备跳转到OAuth提供商:', data.authorization_url)
      window.location.href = data.authorization_url
    },
    onError: (error) => {
      console.error('❌ [OAuth-Frontend] OAuth登录失败:', error)
    }
  })
}

export const useOAuthCallback = () => {
  const queryClient = useQueryClient()

  return useMutation<AuthResponse, Error, { provider: string; code: string; state: string }>({
    mutationFn: ({ provider, code, state }) => userApi.handleOAuthCallback(provider, { code, state }),
    onSuccess: (data) => {
      // Clear all React Query caches before setting new user data
      queryClient.clear()

      // Save token
      localStorage.setItem('authToken', data.access_token)
      localStorage.setItem('loginTime', new Date().toISOString())
      localStorage.setItem('rememberMe', 'true')

      // Set user data to query cache to avoid additional API call
      if (data.user) {
        queryClient.setQueryData(['profile'], data.user)
      }
    },
  })
}

export const useOAuthProviders = () => {
  return useQuery<OAuthProvider[], Error>({
    queryKey: ['oauth-providers'],
    queryFn: userApi.getOAuthProviders,
    staleTime: 5 * 60 * 1000, // 5分钟内不重新获取
  })
}

export const useAuthConfig = () => {
  return useQuery<{ enable_local_auth: boolean; oauth_providers: OAuthProvider[] }, Error>({
    queryKey: ['auth-config'],
    queryFn: userApi.getAuthConfig,
  })
}

export const useProfile = () => {
  return useQuery<User, Error>({
    queryKey: ['profile'],
    queryFn: userApi.getProfile,
    enabled: !!localStorage.getItem('authToken'),
    retry: false,
  })
}

export const useLogout = () => {
  return {
    logout: userApi.logout,
  }
}

export const useLinkOAuth = () => {
  const queryClient = useQueryClient()
  return useMutation<User, Error, { provider: string; data: LinkOAuthData }>({
    mutationFn: ({ provider, data }) => userApi.linkOAuthAccount(provider, data),
    onSuccess: (data) => {
      queryClient.setQueryData(['profile'], data)
      queryClient.invalidateQueries({ queryKey: ['profile'] })
    },
  })
}

export const useSetCredentials = () => {
  const queryClient = useQueryClient()
  return useMutation<User, Error, SetCredentialsData>({
    mutationFn: (data) => userApi.setLoginCredentials(data),
    onSuccess: (data) => {
      queryClient.setQueryData(['profile'], data)
      queryClient.invalidateQueries({ queryKey: ['profile'] })
    },
  })
}